/**
 * Extract a dominant, contrasting color from an image (logo).
 *
 * Strategy:
 * 1. Downscale image to a small canvas for performance.
 * 2. Skip transparent + near-white + near-black pixels (likely background or outline).
 * 3. Skip the outermost ~10% of the image — that's typically the immediate outline.
 * 4. Quantize remaining pixels and weight them by saturation × frequency, so we
 *    prefer vibrant brand colors over neutral grays.
 * 5. Return the winning color as a hex string.
 *
 * @param {string} imageSrc - data URL or http(s) URL of the image
 * @returns {Promise<string>} hex color, e.g. "#c8102e"
 */
export async function extractDominantColor(imageSrc) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const TARGET = 64;
        const canvas = document.createElement("canvas");
        const scale = Math.min(TARGET / img.width, TARGET / img.height, 1);
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const { data, width, height } = ctx.getImageData(
          0, 0, canvas.width, canvas.height
        );

        // Skip outermost 10% on each side as likely outline.
        const padX = Math.round(width * 0.1);
        const padY = Math.round(height * 0.1);

        const buckets = new Map();
        // Quantize to 24 levels per channel to merge near-identical colors.
        const Q = 24;

        for (let y = padY; y < height - padY; y++) {
          for (let x = padX; x < width - padX; x++) {
            const i = (y * width + x) * 4;
            const a = data[i + 3];
            if (a < 200) continue; // skip transparent

            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            // Skip near-white & near-black
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            if (max > 240 && min > 240) continue; // ~white
            if (max < 25) continue; // ~black

            const qr = Math.round(r / Q) * Q;
            const qg = Math.round(g / Q) * Q;
            const qb = Math.round(b / Q) * Q;
            const key = `${qr},${qg},${qb}`;

            const sat = max === 0 ? 0 : (max - min) / max; // HSV saturation
            const lum = (qr + qg + qb) / 3;

            const cur = buckets.get(key) || { count: 0, sat, r: qr, g: qg, b: qb, lum };
            cur.count += 1;
            buckets.set(key, cur);
          }
        }

        if (buckets.size === 0) {
          resolve("#1e3a5f");
          return;
        }

        // Score by frequency × (saturation + 0.15) so vibrant colors win
        // over grayscale, but a strong gray can still win if it dominates.
        let best = null;
        let bestScore = -1;
        for (const v of buckets.values()) {
          const score = v.count * (v.sat + 0.15);
          if (score > bestScore) {
            bestScore = score;
            best = v;
          }
        }

        const toHex = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
        resolve(`#${toHex(best.r)}${toHex(best.g)}${toHex(best.b)}`);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error("Failed to load image for color extraction"));
    img.src = imageSrc;
  });
}
